import React, { useState, useEffect } from 'react';
import { PAIEMENT_ACTIF } from '../config/features';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '../i18n/router';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  Avatar,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { recupererListeVoyageurs, checkUserSociete } from '../Services';
import axios from 'axios'; // <-- On va poster directement le FormData via axios
import { Voyageurs } from '../models/Voyageurs';
import AssuranceVisaComponent from '../components/AssuranceVisaComponent';
import { API_BASE_URL } from '../config/api';
interface FormScreenRouteParams {
  title: string;
  prixconsulat: number;
  prixintervention: number;
  demandeId: number;
  url_flag: string;
  validite: string;
  informations: string;
  commentaires?: string;
  titleDisplay?: string;
}

const FormInde: React.FC = () => {
  const { t } = useTranslation('formulaires');
  const { t: tp } = useTranslation('formulaires_pays');
  // Récupération des paramètres passés en state via react-router
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromVoyageurCreation = searchParams.get('fromCreation') === 'true';

  // Essayer de récupérer le state depuis location.state ou sessionStorage
  const getInitialState = (): FormScreenRouteParams => {
    // Si on revient de la création de voyageur, utiliser sessionStorage
    if (fromVoyageurCreation) {
      const saved = sessionStorage.getItem('formState');
      if (saved) {
        return JSON.parse(saved) as FormScreenRouteParams;
      }
    }
    // Sinon utiliser location.state et nettoyer sessionStorage
    if (location.state && Object.keys(location.state).length > 0) {
      sessionStorage.removeItem('formState');
      return location.state as FormScreenRouteParams;
    }
    return {} as FormScreenRouteParams;
  };
  const state = getInitialState();
  const titleDisplay = (state as any)?.titleDisplay || (state as any)?.title || '';
  const demandeId = state.demandeId;
  const isEditMode = demandeId != null;

  // États initiaux (issus des paramètres de route ou valeurs par défaut)
  const [title, setTitle] = useState<string>(state?.title || '');
  const [prixconsulat, setPrixconsulat] = useState<number>(state?.prixconsulat || 0);
  const [prixintervention, setPrixintervention] = useState<number>(state?.prixintervention || 0);
  const [urlFlag, setUrlFlag] = useState<string>(state?.url_flag || '');
  const [validite, setValidite] = useState<string>(state?.validite || '');
  const commentaires = state?.commentaires || '';
  const [informations, setInformations] = useState<string>(state?.informations || '');

  const [dateDepart, setDateDepart] = useState<string>('');
  const [dateArriveeInde, setDateArriveeInde] = useState<string>('');
  const [dateSortie, setDateSortie] = useState<string>('');
  const [voyageurs, setVoyageurs] = useState<Voyageurs[]>([]);
  const [selectedVoyageur, setSelectedVoyageur] = useState<string>('');
  const [champsNonRemplis, setChampsNonRemplis] = useState<string[]>([]);
  const [assuranceVisa, setAssuranceVisa] = useState<string>('');
  const [sliderValue, setSliderValue] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hideFees] = useState<boolean>(false);

  // États pour la gestion du type d'utilisateur et l'écran de succès
  const [isClientSociete, setIsClientSociete] = useState<boolean | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [createdDemandeId, setCreatedDemandeId] = useState<number | null>(null);

  // États pour d'autres champs spécifiques
  const [lieuArrivee, setLieuArrivee] = useState<string>('');
  const [nomEmployeur, setNomEmployeur] = useState<string>('');
  const [adresseEmployeur, setAdresseEmployeur] = useState<string>('');
  const [codePostalEmployeur, setCodePostalEmployeur] = useState<string>('');
  const [villeEmployeur, setVilleEmployeur] = useState<string>('');
  const [telephoneEmployeur, setTelephoneEmployeur] = useState<string>('');
  const [nomSteInde, setNomSteInde] = useState<string>('');
  const [adresseSteInde, setAdresseSteInde] = useState<string>('');
  const [codePostalSteInde, setCodePostalSteInde] = useState<string>('');
  const [villeSteInde, setVilleSteInde] = useState<string>('');
  const [telephoneSteInde, setTelephoneSteInde] = useState<string>('');
  const [nomPrenomContactUrgence, setNomPrenomContactUrgence] = useState<string>('');
  const [adresseContact, setAdresseContact] = useState<string>('');
  const [codePostalContact, setCodePostalContact] = useState<string>('');
  const [villeContact, setVilleContact] = useState<string>('');
  const [telephoneContact, setTelephoneContact] = useState<string>('');
  const [nomHotel, setNomHotel] = useState<string>('');
  const [adresseHotel, setAdresseHotel] = useState<string>('');

  // Pour la lettre d'invitation (fichier)
  const [letterInvitationFile, setLetterInvitationFile] = useState<File | null>(null);
  // Pour un éventuel aperçu local
  const [letterInvitationPreview, setLetterInvitationPreview] = useState<string | null>(null);
  const [uploadedFileType, setUploadedFileType] = useState<'image' | 'pdf' | null>(null);

  // Calcul du total
  const total =
    prixconsulat + prixintervention + (assuranceVisa.toLowerCase() === 'oui' ? 20 : 0);

  // Validation du formulaire
  const validateForm = () => {
    const parseDate = (dateStr: string): Date | null => {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      }
      return null;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const departDate = parseDate(dateDepart);
    const sortieDate = parseDate(dateSortie);

    const errors: string[] = [];

    // Contrôles de dates
    if (departDate) {
      if (isNaN(departDate.getTime()) || departDate <= today) {
        errors.push(tp('inde.errors.departureDateInvalid'));
      }
    } else {
      errors.push(tp('inde.errors.departureDateRequired'));
    }

    if (dateSortie && sortieDate) {
      if (isNaN(sortieDate.getTime()) || (departDate && sortieDate <= departDate)) {
        errors.push(tp('inde.errors.returnDateInvalid'));
      }
    } else if (dateSortie && !sortieDate) {
      errors.push(tp('inde.errors.returnDateRequired'));
    }

    // Champs obligatoires
    if (!selectedVoyageur) errors.push(t('common.validation.travelerNotSelected'));
    if (!dateDepart.trim()) errors.push(t('common.validation.departureDate'));
    if (!dateSortie.trim()) errors.push(tp('inde.validation.dateSortie'));
    if (!lieuArrivee.trim()) errors.push(tp('inde.validation.lieuArrivee'));
    if (!nomEmployeur.trim()) errors.push(tp('inde.validation.nomEmployeur'));
    if (!adresseEmployeur.trim()) errors.push(tp('inde.validation.adresseEmployeur'));
    if (!codePostalEmployeur.trim()) errors.push(tp('inde.validation.codePostalEmployeur'));
    if (!villeEmployeur.trim()) errors.push(tp('inde.validation.villeEmployeur'));
    if (!telephoneEmployeur.trim()) errors.push(tp('inde.validation.telephoneEmployeur'));

    if (!nomPrenomContactUrgence.trim()) errors.push(tp('inde.validation.nomPrenomContactUrgence'));
    if (!adresseContact.trim()) errors.push(tp('inde.validation.adresseContact'));
    if (!codePostalContact.trim()) errors.push(tp('inde.validation.codePostalContact'));
    if (!villeContact.trim()) errors.push(tp('inde.validation.villeContact'));
    if (!telephoneContact.trim()) errors.push(tp('inde.validation.telephoneContact'));

    // Si c'est un visa affaire
    if (title.toLowerCase().includes('affaire')) {
      // Fichier invitation obligatoire
      if (!letterInvitationFile) {
        errors.push(tp('inde.validation.lettreInvitation'));
      }
      // Les infos société en Inde
      if (!nomSteInde.trim()) errors.push(tp('inde.validation.nomSteInde'));
      if (!adresseSteInde.trim()) errors.push(tp('inde.validation.adresseSteInde'));
      if (!codePostalSteInde.trim()) errors.push(tp('inde.validation.codePostalSteInde'));
      if (!villeSteInde.trim()) errors.push(tp('inde.validation.villeSteInde'));
      if (!telephoneSteInde.trim()) errors.push(tp('inde.validation.telephoneSteInde'));
    }

    // Si c'est un visa tourisme
    if (title.toLowerCase().includes('tourisme')) {
      if (!nomHotel.trim()) errors.push(tp('inde.validation.nomHotel'));
      if (!adresseHotel.trim()) errors.push(tp('inde.validation.adresseHotel'));
    }

    // Assurance
    if (!assuranceVisa.trim()) errors.push(t('common.validation.insurance'));
    if (!sliderValue) errors.push(t('common.validation.cguAcceptance'));

    setChampsNonRemplis(errors);
    setIsFormValid(errors.length === 0);
  };

  // Revalidation à chaque changement de champs
  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateDepart,
    dateArriveeInde,
    dateSortie,
    assuranceVisa,
    sliderValue,
    selectedVoyageur,
    lieuArrivee,
    nomEmployeur,
    adresseEmployeur,
    codePostalEmployeur,
    villeEmployeur,
    telephoneEmployeur,
    nomSteInde,
    adresseSteInde,
    codePostalSteInde,
    villeSteInde,
    telephoneSteInde,
    nomPrenomContactUrgence,
    adresseContact,
    codePostalContact,
    villeContact,
    telephoneContact,
    nomHotel,
    adresseHotel,
    letterInvitationFile,
  ]);

  // Récupération de la liste des voyageurs
  useEffect(() => {
    const fetchVoyageurs = async () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        const listeVoyageurs = await recupererListeVoyageurs(user.id_utilisateur);
        setVoyageurs(listeVoyageurs);
        // Sélection automatique s'il n'y a qu'un seul voyageur
        if (listeVoyageurs.length === 1) {
          setSelectedVoyageur(listeVoyageurs[0].id.toString());
        } else {
          setSelectedVoyageur('');
        }
      }
    };
    fetchVoyageurs();
  }, [demandeId]);

  // Vérification si l'utilisateur appartient à une société
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
          const user = JSON.parse(userJson);
          const societeResult = await checkUserSociete(user.id_utilisateur);
          setIsClientSociete(societeResult.isSociete);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut utilisateur:', error);
        setIsClientSociete(true); // Par défaut, considérer comme société
      }
    };
    checkUserStatus();
  }, []);

  const selectedVoyageurObj = voyageurs.find(v => v.id.toString() === selectedVoyageur);

  // Empêcher la fermeture de l'onglet sans confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Bouton retour
  const handleBack = () => {
    if (
      window.confirm(t('common.confirmation.unsavedChanges'))
    ) {
      navigate(-1);
    }
  };

  // Gestion de l'upload du fichier (lettre d'invitation) + prévisualisation
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setUploadedFileType('image');
      } else if (file.type === 'application/pdf') {
        setUploadedFileType('pdf');
      } else {
        setUploadedFileType(null);
      }
      // Stocke le File
      setLetterInvitationFile(file);

      // URL locale pour aperçu
      const fileUrl = URL.createObjectURL(file);
      setLetterInvitationPreview(fileUrl);
    }
  };

  // Formatage de la date (jj/mm/aaaa)
  const formatDate = (input: string) => {
    const numbers = input.replace(/[^\d]/g, '');
    let formatted = '';
    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += numbers[i];
    }
    return formatted;
  };

  // Soumission
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      let userId: string | null = null;
      if (userJson) {
        const user = JSON.parse(userJson);
        userId = user.id_utilisateur;
      }

      const etatFormulaire = champsNonRemplis.length === 0;

      // Construction du FormData
      const formData = new FormData();
      // On y append tous les champs texte
      formData.append('etatFormulaire', String(etatFormulaire));
      formData.append('idUtilisateur', userId || '');
      formData.append('idVoyageur', selectedVoyageur);
      formData.append('titreVisa', title);
      formData.append('dateDepart', dateDepart);
      formData.append('dateArriveeInde', dateArriveeInde);
      formData.append('dateSortie', dateSortie);
      formData.append('lieuArrivee', lieuArrivee);
      formData.append('nomEmployeur', nomEmployeur);
      formData.append('adresseEmployeur', adresseEmployeur);
      formData.append('codePostalEmployeur', codePostalEmployeur);
      formData.append('villeEmployeur', villeEmployeur);
      formData.append('telephoneEmployeur', telephoneEmployeur);
      formData.append('nomPrenomContactUrgence', nomPrenomContactUrgence);
      formData.append('adresseContact', adresseContact);
      formData.append('codePostalContact', codePostalContact);
      formData.append('villeContact', villeContact);
      formData.append('telephoneContact', telephoneContact);
      formData.append('assuranceVisa', assuranceVisa);
      formData.append('prixconsulat', String(prixconsulat));
      formData.append('prixintervention', String(prixintervention));
      formData.append('validite', validite);

      // Si visa affaire => société Inde + lettre d'invitation
      if (title.toLowerCase().includes('affaire')) {
        formData.append('nomSteInde', nomSteInde);
        formData.append('adresseSteInde', adresseSteInde);
        formData.append('codePostalSteInde', codePostalSteInde);
        formData.append('villeSteInde', villeSteInde);
        formData.append('telephoneSteInde', telephoneSteInde);

        // Ajout du fichier
        if (letterInvitationFile) {
          formData.append('lettreInvitation', letterInvitationFile);
        }
      }

      // Si visa tourisme => infos hôtel
      if (title.toLowerCase().includes('tourisme')) {
        formData.append('nomHotel', nomHotel);
        formData.append('adresseHotel', adresseHotel);
      }

      // Envoi vers le backend (à adapter selon votre URL)
      const response = await axios.post(`${API_BASE_URL}/validerDemande`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setIsLoading(false);

      // Stocker l'ID de la demande créée si disponible
      if (response && response.data && response.data.idDemande) {
        setCreatedDemandeId(response.data.idDemande);
      }

      // Afficher l'écran de succès au lieu de naviguer directement
      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Erreur lors de la validation de la demande:', error);
      setIsLoading(false);
    }
  };

  // Navigation vers le paiement SumUp (pour particuliers hors société)
  const handlePayerEnLigne = () => {
    if (createdDemandeId) {
      navigate('/paiement', {
        state: {
          bonId: 0,
          urlPaiement: '',
          demandeIds: [createdDemandeId],
          modePaiementSumup: true,
        },
      });
    } else {
      navigate('/retour', { state: { tabValue: 2, showPaymentMessage: true } });
    }
  };

  // Créer une nouvelle demande (reste sur le même formulaire)
  const handleNouvelleDemande = () => {
    setShowSuccessScreen(false);
    setCreatedDemandeId(null);
    setDateDepart('');
    setDateArriveeInde('');
    setDateSortie('');
    setSelectedVoyageur(voyageurs.length === 1 ? voyageurs[0].id.toString() : '');
    setAssuranceVisa('');
    setSliderValue(false);
    setLieuArrivee('');
    setNomEmployeur('');
    setAdresseEmployeur('');
    setCodePostalEmployeur('');
    setVilleEmployeur('');
    setTelephoneEmployeur('');
    setNomSteInde('');
    setAdresseSteInde('');
    setCodePostalSteInde('');
    setVilleSteInde('');
    setTelephoneSteInde('');
    setNomPrenomContactUrgence('');
    setAdresseContact('');
    setCodePostalContact('');
    setVilleContact('');
    setTelephoneContact('');
    setNomHotel('');
    setAdresseHotel('');
    setLetterInvitationFile(null);
    setLetterInvitationPreview(null);
    setUploadedFileType(null);
  };

  // Juste une fonction vide pour le onClick si disabled
  const disabledPressHandler = () => {};

  // Si l'écran de succès est affiché
  if (showSuccessScreen) {
    return (
      <Box
        sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
      >
        <Container maxWidth="md">
          <Box sx={{ backgroundColor: '#fff', p: 4, borderRadius: 2, boxShadow: 2, textAlign: 'center' }}>
            {/* En-tête avec drapeau */}
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 60, height: 36, mb: 2, mx: 'auto' }}
              />
            )}

            {/* Message de succès */}
            <Typography variant="h4" sx={{ color: 'green', fontWeight: 'bold', mb: 2 }}>
              {t('success.title')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t('success.message', { title: titleDisplay })}
            </Typography>

            {/* Boutons conditionnels selon le type d'utilisateur */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', mt: 4 }}>
              {/* Bouton Payer en ligne - uniquement pour particuliers (hors société) */}
              {isClientSociete === false && !hideFees && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handlePayerEnLigne}
                  sx={{ minWidth: 280 }}
                >
                  {PAIEMENT_ACTIF ? t('success.payOnline') : t('success.launchDemande')}
                </Button>
              )}

              {/* Bouton Créer une nouvelle demande - pour tous les utilisateurs */}
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleNouvelleDemande}
                sx={{ minWidth: 280 }}
              >
                {t('success.newRequest')}
              </Button>

              {/* Bouton Voir mes demandes */}
              <Button
                variant="text"
                color="inherit"
                onClick={() => navigate('/retour', { state: { tabValue: 2 } })}
                sx={{ mt: 1 }}
              >
                {t('success.viewRequests')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{ backgroundColor: '#ECE4D3', minHeight: '100vh', py: 4 }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            backgroundColor: '#fff',
            p: 3,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {urlFlag && (
              <Box
                component="img"
                src={urlFlag}
                alt={t('common.alt.flag')}
                sx={{ width: 50, height: 30, mb: 1, mx: 'auto' }}
              />
            )}
            <Typography variant="h5" component="h1" gutterBottom>
              {t('common.formTitle', { title: titleDisplay })}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {validite}
              <br />
              {informations}
            </Typography>
            <Button variant="outlined" onClick={handleBack} sx={{ mt: 1 }}>
              {t('common.back')}
            </Button>
          </Box>

          {/* Affichage des commentaires du visa en HTML */}
          {commentaires && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: '#fff3cd',
                borderRadius: 1,
                border: '1px solid #ffc107',
              }}
              dangerouslySetInnerHTML={{ __html: commentaires }}
            />
          )}

          {/* Sélection du voyageur si non en mode édition */}
          {!isEditMode && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="voyageur-select-label">{t('common.selectTraveler')}</InputLabel>
                <Select
                  labelId="voyageur-select-label"
                  id="voyageur-select"
                  value={selectedVoyageur}
                  label={t('common.selectTraveler')}
                  onChange={(e: SelectChangeEvent) => setSelectedVoyageur(e.target.value)}
                >
                  {voyageurs.map(v => (
                    <MenuItem key={v.id} value={v.id.toString()}>
                      {v.nom} {v.prenom}
                      {v.numeropax ? t('common.selectTravelerWithPassport', { passport: v.numeropax }) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Bouton pour créer un nouveau voyageur */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    sessionStorage.setItem('formState', JSON.stringify({
                      title,
                      prixconsulat,
                      prixintervention,
                      url_flag: urlFlag,
                      validite,
                      informations,
                      commentaires
                    }));
                    navigate(`/formulaire_creation_voyageur?returnUrl=${location.pathname}`);
                  }}
                  sx={{
                    color: '#D89D2F',
                    borderColor: '#D89D2F',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#B5811F'
                    }
                  }}
                >
                  {t('common.createNewTraveler')}
                </Button>
              </Box>
            </>
          )}

          {/* Avatar du voyageur sélectionné */}
          {selectedVoyageurObj && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={selectedVoyageurObj.url || 'https://example.com/default-avatar.png'}
                alt={t('common.alt.avatar')}
                sx={{ width: 100, height: 100 }}
              />
            </Box>
          )}

          <TextField
            label={t('common.departureDate.label')}
            placeholder={t('common.departureDate.placeholder')}
            value={dateDepart}
            onChange={(e) => setDateDepart(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.dates.arriveeIndeLabel')}
            placeholder={tp('inde.dates.arriveeIndePlaceholder')}
            value={dateArriveeInde}
            onChange={(e) => setDateArriveeInde(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('common.returnDate.label')}
            placeholder={t('common.returnDate.placeholder')}
            value={dateSortie}
            onChange={(e) => setDateSortie(formatDate(e.target.value))}
            fullWidth
            margin="normal"
          />

          {/* Upload du fichier lettre d'invitation (si visa affaire) */}
          {title.toLowerCase().includes('affaire') && (
            <Box sx={{ my: 2 }}>
              <Typography variant="body1" gutterBottom>
                {tp('inde.fields.lettreInvitationLabel')}
              </Typography>
              <input type="file" accept="image/*,application/pdf" onChange={handleFileUpload} />
              {letterInvitationPreview && (
                <Box sx={{ mt: 2 }}>
                  {uploadedFileType === 'image' ? (
                    <img
                      src={letterInvitationPreview}
                      alt={tp('inde.fields.lettreInvitationPreviewAlt')}
                      style={{ maxWidth: '100%', maxHeight: 300 }}
                    />
                  ) : uploadedFileType === 'pdf' ? (
                    <object
                      data={letterInvitationPreview}
                      type="application/pdf"
                      width="100%"
                      height="500"
                    >
                      <p>
                        {tp('inde.fields.pdfPrefix')}
                        <a
                          href={letterInvitationPreview}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {tp('inde.fields.pdfViewLink')}
                        </a>
                      </p>
                    </object>
                  ) : null}
                </Box>
              )}
            </Box>
          )}

          <TextField
            label={tp('inde.fields.lieuArrivee.label')}
            placeholder={tp('inde.fields.lieuArrivee.placeholder')}
            value={lieuArrivee}
            onChange={(e) => setLieuArrivee(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Section Employeur */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {tp('inde.sections.employeur')}
          </Typography>

          <TextField
            label={tp('inde.fields.nomEmployeur.label')}
            placeholder={tp('inde.fields.nomEmployeur.placeholder')}
            value={nomEmployeur}
            onChange={(e) => setNomEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.adresseEmployeur.label')}
            placeholder={tp('inde.fields.adresseEmployeur.placeholder')}
            value={adresseEmployeur}
            onChange={(e) => setAdresseEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.codePostalEmployeur.label')}
            placeholder={tp('inde.fields.codePostalEmployeur.placeholder')}
            value={codePostalEmployeur}
            onChange={(e) => setCodePostalEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.villeEmployeur.label')}
            placeholder={tp('inde.fields.villeEmployeur.placeholder')}
            value={villeEmployeur}
            onChange={(e) => setVilleEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.telephoneEmployeur.label')}
            placeholder={tp('inde.fields.telephoneEmployeur.placeholder')}
            value={telephoneEmployeur}
            onChange={(e) => setTelephoneEmployeur(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Champs société en Inde (si visa affaire) */}
          {title.toLowerCase().includes('affaire') && (
            <>
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                {tp('inde.sections.societeInde')}
              </Typography>

              <TextField
                label={tp('inde.fields.nomSteInde.label')}
                placeholder={tp('inde.fields.nomSteInde.placeholder')}
                value={nomSteInde}
                onChange={(e) => setNomSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('inde.fields.adresseSteInde.label')}
                placeholder={tp('inde.fields.adresseSteInde.placeholder')}
                value={adresseSteInde}
                onChange={(e) => setAdresseSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('inde.fields.codePostalSteInde.label')}
                placeholder={tp('inde.fields.codePostalSteInde.placeholder')}
                value={codePostalSteInde}
                onChange={(e) => setCodePostalSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('inde.fields.villeSteInde.label')}
                placeholder={tp('inde.fields.villeSteInde.placeholder')}
                value={villeSteInde}
                onChange={(e) => setVilleSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('inde.fields.telephoneSteInde.label')}
                placeholder={tp('inde.fields.telephoneSteInde.placeholder')}
                value={telephoneSteInde}
                onChange={(e) => setTelephoneSteInde(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          )}

          {/* Champs hôtel (si visa tourisme) */}
          {title.toLowerCase().includes('tourisme') && (
            <>
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                {tp('inde.sections.hotel')}
              </Typography>

              <TextField
                label={tp('inde.fields.nomHotel.label')}
                placeholder={tp('inde.fields.nomHotel.placeholder')}
                value={nomHotel}
                onChange={(e) => setNomHotel(e.target.value)}
                fullWidth
                margin="normal"
              />
              <TextField
                label={tp('inde.fields.adresseHotel.label')}
                placeholder={tp('inde.fields.adresseHotel.placeholder')}
                value={adresseHotel}
                onChange={(e) => setAdresseHotel(e.target.value)}
                fullWidth
                margin="normal"
              />
            </>
          )}

          {/* Section Contact d'urgence */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {tp('inde.sections.contactUrgence')}
          </Typography>

          <TextField
            label={tp('inde.fields.nomPrenomContactUrgence.label')}
            placeholder={tp('inde.fields.nomPrenomContactUrgence.placeholder')}
            value={nomPrenomContactUrgence}
            onChange={(e) => setNomPrenomContactUrgence(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.adresseContact.label')}
            placeholder={tp('inde.fields.adresseContact.placeholder')}
            value={adresseContact}
            onChange={(e) => setAdresseContact(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.codePostalContact.label')}
            placeholder={tp('inde.fields.codePostalContact.placeholder')}
            value={codePostalContact}
            onChange={(e) => setCodePostalContact(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.villeContact.label')}
            placeholder={tp('inde.fields.villeContact.placeholder')}
            value={villeContact}
            onChange={(e) => setVilleContact(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={tp('inde.fields.telephoneContact.label')}
            placeholder={tp('inde.fields.telephoneContact.placeholder')}
            value={telephoneContact}
            onChange={(e) => setTelephoneContact(e.target.value)}
            fullWidth
            margin="normal"
          />

          {/* Module Assurance */}
          <Box sx={{ my: 2 }}>
            <AssuranceVisaComponent
              assuranceVisa={assuranceVisa}
              setAssuranceVisa={setAssuranceVisa}
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
            />
          </Box>

          {/* Récapitulatif du total */}
          {!hideFees && (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Typography variant="body1">{t('common.fees.visa', { prix: prixconsulat })}</Typography>
              <Typography variant="body1">{t('common.fees.service', { prix: prixintervention })}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                {t('common.fees.total', { total })}
              </Typography>
            </Box>
          )}

          {/* État du formulaire (incomplet / complet) */}
          <Box
            sx={{
              my: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: champsNonRemplis.length > 0 ? '#ffcccb' : '#ccffcc',
            }}
          >
            {champsNonRemplis.length > 0 ? (
              <>
                <Typography variant="h6" align="center">
                  {t('common.formStatus.incomplete')}
                </Typography>
                {champsNonRemplis.map((champ, index) => (
                  <Typography key={index}>{champ}</Typography>
                ))}
              </>
            ) : (
              <Typography align="center">{t('common.formStatus.complete')}</Typography>
            )}
          </Box>

          {/* Bouton de validation */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', my: 2 }}>
              <Button
                variant="contained"
                onClick={isFormValid ? handleSubmit : disabledPressHandler}
                disabled={!isFormValid}
                sx={{ backgroundColor: isFormValid ? 'black' : '#A9A9A9' }}
              >
                {t('common.submit')}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormInde;